import React from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Fab,
  Badge,
} from '@mui/material';
import {
  ArrowBack,
  DarkMode,
  LightMode,
  ShoppingCart,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCustomTheme } from '../../contexts/ThemeContext';

const TableOrderLayout = ({ title, subtitle, children, showBackButton = true, showCart = false, cartItemCount = 0 }) => {
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <Box>
      {/* Simple Navigation Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(45, 27, 20, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {showBackButton && (
              <IconButton
                color="inherit"
                onClick={() => navigate('/')}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ArrowBack />
              </IconButton>
            )}
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              {title || 'Table Order'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              {isDarkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      {/* <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '30vh', sm: '40vh', md: '50vh' },
          mt: 8, // Account for fixed AppBar
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #2d1b14 0%, #1a0f0a 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            zIndex: 1,
          },
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            zIndex: 2, 
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, sm: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            width: '100%'
          }}
        >
          {subtitle && (
            <Typography
              variant={isMobile ? "body2" : "h6"}
              sx={{ 
                textAlign: 'center',
                opacity: 0.9,
                maxWidth: { xs: '100%', sm: '600px', md: '800px' },
                mx: 'auto',
                mb: { xs: 2, sm: 3, md: 4 },
                fontSize: { xs: '0.85rem', sm: '1rem', md: '1.125rem' },
                lineHeight: { xs: 1.4, sm: 1.5 },
                px: { xs: 1, sm: 0 },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Container>
      </Box> */}

      {/* Content Section */}
      <Box sx={{ mt: 8, minHeight: '100vh' }}>
        <Container 
          maxWidth="lg" 
          sx={{ 
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 1, sm: 2, md: 3 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Paper
            elevation={isDarkMode ? 1 : 3}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              mx: { xs: 0.5, sm: 0 },
              minHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 140px)' },
            }}
          >
            {children}
          </Paper>
        </Container>
      </Box>

      {/* Floating Cart Button */}
      {showCart && (
        <Fab
          color="primary"
          aria-label="cart"
          onClick={() => navigate('/table-checkout')}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1000,
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 6px 25px rgba(0,0,0,0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <Badge
            badgeContent={cartItemCount}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 18, sm: 20 },
                height: { xs: 18, sm: 20 },
              }
            }}
          >
            <ShoppingCart sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
          </Badge>
        </Fab>
      )}
    </Box>
  );
};

export default TableOrderLayout;

